// On-chain Twetch media, resolved to live URLs.
//
// Twetch's original CDNs are dead, but the media is content-addressed on
// chain (B:// protocol), so refs re-resolve to Twetch's own media proxy —
// `api.twetch.com/v1/media/<txid>[.ext]` — with media.ordinalswallet.com
// mirroring the legacy CDN slugs. This is a deliberate port of the resolution
// rules in Twetch's own web client (`web/src/util.ts`), trimmed to the ref
// shapes the feed actually serves; the `?v=` cache keys must match theirs so
// this app hits the same warmed Cloudflare objects instead of re-pulling
// every image through the origin.

import { TWETCH_API, TWETCH_WEB, type TwetchPost } from './twetch-api'

const OW_MEDIA = 'https://media.ordinalswallet.com'
const OW_INSCRIPTION_CONTENT = 'https://cdn.ordinalswallet.com/inscription/content'
/// Cloudflare cache key version — matches Twetch's web client (v4: origin
/// emits image dimension headers). Bump ONLY in lockstep with theirs.
const MEDIA_VER = '4'

const mediaTx = (tx: string) => `${TWETCH_API}/v1/media/${tx.toLowerCase()}.jpg?v=${MEDIA_VER}`
const mediaTxOut = (tx: string, vout: number) =>
	`${TWETCH_API}/v1/media/${tx.toLowerCase()}-o${vout}.jpg?v=${MEDIA_VER}`

function txid64(s: string): string | null {
	const m = s.match(/[a-f0-9]{64}/i)
	return m ? m[0] : null
}

/// Inscriptions are mostly pixel art: scaled with smoothing they blur, so a
/// resolved inscription image renders with hard pixels. Photos (every other
/// avatar source) keep normal smoothing.
export function pixelArtClass(url: string | null | undefined): string {
	return url?.startsWith(OW_INSCRIPTION_CONTENT) ? '[image-rendering:pixelated]' : ''
}

/// Resolve any Twetch image ref (post media, avatar icon) to a live URL.
/// Returns null when the ref names nothing servable.
export function resolveTwetchImage(ref: string | null | undefined): string | null {
	const s = (ref ?? '').toString().trim()
	if (!s) {
		return null
	}
	// ord://<inscriptionId> — a Bitcoin inscription avatar, from our own CDN.
	const ord = /^ord:\/\/([a-f0-9]{64}i\d{1,10})$/i.exec(s)
	if (ord) {
		return `${OW_INSCRIPTION_CONTENT}/${ord[1].toLowerCase()}`
	}
	// b://<txid>@<vout> — one file of a multi-file post tx. Only the media
	// proxy can address an output; tx-level gateways would serve the wrong
	// bytes.
	const vout = /^(?:b:\/\/)?([a-f0-9]{64})@(\d{1,3})$/i.exec(s)
	if (vout) {
		return mediaTxOut(vout[1], Number(vout[2]))
	}
	// b://<txid> — B:// content by txid.
	if (s.startsWith('b://')) {
		const tx = txid64(s.slice(4))
		return tx ? mediaTx(tx) : null
	}
	// Legacy Twetch CDN hosts — mirrored by Ordinals Wallet's own CDN.
	if (s.includes('media.twetch.app')) {
		return s.replace(/^http:/, 'https:').replace('media.twetch.app', 'media.ordinalswallet.com')
	}
	if (s.includes('cimg.twetch.com')) {
		return s.replace(/^http:/, 'https:').replace('cimg.twetch.com', 'media.ordinalswallet.com')
	}
	// Dead dogefiles CDN — the on-chain txid inside the URL still serves.
	if (s.includes('dogefiles.twetch.app')) {
		const tx = txid64(s)
		return tx ? mediaTx(tx) : null
	}
	// Any other absolute URL — pass through, https forced.
	if (s.startsWith('http')) {
		return s.replace(/^http:/, 'https:')
	}
	// Bare 64-hex txid — on-chain via the media proxy.
	if (/^[a-f0-9]{64}$/i.test(s)) {
		return mediaTx(s)
	}
	// CDN path slug ("<sha256>.webp" and friends) — the stored form for CDN
	// media; compose the mirror base. A leading "/" would be a local app
	// asset, which this feed never carries — treat as unservable.
	if (!s.startsWith('/') && !s.includes('://') && (s.includes('/') || /\.[a-z0-9]{2,5}$/i.test(s))) {
		return `${OW_MEDIA}/${s}`
	}
	return null
}

/// Parse a post's `files` JSON into its raw media refs.
export function postFileRefs(files: string | null | undefined): string[] {
	if (!files) {
		return []
	}
	try {
		const arr: unknown = JSON.parse(files)
		if (!Array.isArray(arr)) {
			return []
		}
		return arr.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
	} catch {
		return []
	}
}

const CONTENT_MEDIA_REF = /(^|\s)(b:\/\/[a-f0-9]{64}(?:@\d{1,3})?)(?=\s|$)/gi

/// B:// refs carried inline in post text (signed content). They render as
/// images, so the prose strips them (stripContentMediaRefs) and the media
/// list picks them up here.
export function contentMediaRefs(content: string | null | undefined): string[] {
	if (!content) {
		return []
	}
	const refs: string[] = []
	for (const match of content.matchAll(CONTENT_MEDIA_REF)) {
		const ref = match[2].toLowerCase()
		if (!refs.includes(ref)) {
			refs.push(ref)
		}
	}
	return refs
}

/// Remove standalone media refs from prose — the row renders the media itself.
export function stripContentMediaRefs(content: string): string {
	return content
		.replace(CONTENT_MEDIA_REF, (_match, whitespace: string) => whitespace)
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim()
}

/// The feed's `mediaDims` map keys on a cache id: "<txid>" or "<txid>-o<vout>".
export function dimsCacheId(ref: string): string | null {
	const s = ref.trim()
	const vout = /^(?:b:\/\/)?([a-f0-9]{64})@(\d{1,3})$/i.exec(s)
	if (vout) {
		return `${vout[1].toLowerCase()}-o${vout[2]}`
	}
	const tx = txid64(s)
	return tx ? tx.toLowerCase() : null
}

/// Canonical Twetch permalink — txid when the post has one (stable + shareable),
/// numeric id otherwise. Mirrors Twetch's own postPath().
export function postPermalink(post: Pick<TwetchPost, 'id' | 'txid'>): string {
	const txid = post.txid && post.txid.length === 64 ? post.txid : null
	return `${TWETCH_WEB}/t/${txid ?? post.id}`
}
